import { MgmtApi } from '@/customApiClients/mgmtApiClient/Api';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PillTabsList } from '@/components/ui/pill-tabs-list';
import { SectionHeader } from '@/components/ui/section-header';
import { Tabs, TabsContent } from '@/components/ui/tabs';

import { AgentsTab } from './AgentsTab';
import { CoordinatorTab } from './CoordinatorTab';
import { MemoryStoresTab } from './MemoryStoresTab';
import { ReferenceFilesTab } from './ReferenceFilesTab';
import { SkillsTab } from './SkillsTab';
import { VaultsTab } from './VaultsTab';

interface Props {
    mgmtApi: MgmtApi<unknown>;
}

const AGENT_SETTINGS_TABS = [
    { value: 'cs-coordinator', label: 'Coordinator' },
    { value: 'cs-agents', label: 'Agents' },
    // { value: 'cs-memory', label: 'Memory Stores' },
    { value: 'cs-reference-files', label: 'Reference Files' },
    { value: 'cs-skills', label: 'Skills' },
    { value: 'cs-vaults', label: 'Vaults' }
];

/**
 * Umbrella admin tab for everything Claude Managed Agents related —
 * Coordinator, Agents and Memory Stores each used to be their own top-level
 * Admin tab; grouped here as sub-tabs instead (same nested-Tabs pattern as
 * FeatureAccessTab's User/Group/Organization split) since they're all facets
 * of the same underlying concern.
 *
 * This tab has no scroll area of its own — the title, pills and whichever
 * sub-tab is active all flow together as plain content, and Admin.tsx's
 * single page-level scroll container is what actually scrolls.
 */
export const AgentSettingsTab: React.FC<Props> = ({ mgmtApi }) => {
    // Nothing agent-related works without a workspace API key — whether it
    // comes from the org-level default (OrganizationWorkspaceForManagedAgents)
    // or a per-agent override set from within this very screen. null while
    // the check is in flight; treated as "has a key" on a failed check so a
    // transient network error doesn't lock admins out of the screen that
    // fixes the real problem.
    const [hasApiKey, setHasApiKey] = useState<boolean | null>(null);
    const [apiKeyInput, setApiKeyInput] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;
        fetch('/mgmt/api/v1/Organizations/managed-agent-workspace-status', {
            credentials: 'include'
        })
            .then((res) => (res.ok ? res.json() : { hasApiKey: true }))
            .then((data) => {
                if (!cancelled) setHasApiKey(data?.hasApiKey ?? true);
            })
            .catch(() => {
                if (!cancelled) setHasApiKey(true);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const blocked = hasApiKey === false;

    const handleSaveApiKey = async () => {
        if (!apiKeyInput.trim()) return;
        setSaving(true);
        try {
            const res = await fetch(
                '/mgmt/api/v1/Organizations/managed-agent-workspace-status',
                {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ apiKey: apiKeyInput.trim() })
                }
            );
            if (!res.ok) {
                const detail = await res.json().catch(() => null);
                throw new Error(detail?.detail || 'Failed to save API key');
            }
            setHasApiKey(true);
            toast.success('Workspace API key saved');
        } catch (e: any) {
            toast.error(e?.message || 'Failed to save API key');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="flex flex-col gap-6">
            <SectionHeader
                title="Agent Settings"
                description="Manage Claude Agents — coordinator, sub-agents, memory stores and reference files."
            />
            <Dialog open={blocked}>
                <DialogContent
                    className="sm:max-w-md [&>button]:hidden"
                    onEscapeKeyDown={(e) => e.preventDefault()}
                    onPointerDownOutside={(e) => e.preventDefault()}
                >
                    <DialogHeader>
                        <DialogTitle>No workspace API key set</DialogTitle>
                        <DialogDescription>
                            Your organization doesn't have an Anthropic
                            workspace API key configured yet, so agent settings
                            can't be managed here. Enter the Key Vault secret
                            name holding your organization's Anthropic API key
                            to continue.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-1.5">
                        <Label htmlFor="org-workspace-api-key">
                            Key Vault secret name
                        </Label>
                        <Input
                            id="org-workspace-api-key"
                            value={apiKeyInput}
                            onChange={(e) => setApiKeyInput(e.target.value)}
                            placeholder="anthropic-your-org"
                            disabled={saving}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSaveApiKey();
                            }}
                        />
                    </div>
                    <DialogFooter>
                        <Button size="medium"
                            onClick={handleSaveApiKey}
                            disabled={saving || !apiKeyInput.trim()}
                        >
                            {saving ? 'Saving…' : 'Save'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            {!blocked && (
                <Tabs defaultValue="cs-coordinator" className="flex flex-col">
                    <PillTabsList tabs={AGENT_SETTINGS_TABS} className="mb-8" />

                    <TabsContent value="cs-coordinator">
                        <CoordinatorTab mgmtApi={mgmtApi} />
                    </TabsContent>
                    <TabsContent value="cs-agents">
                        <AgentsTab mgmtApi={mgmtApi} />
                    </TabsContent>
                    <TabsContent value="cs-memory">
                        <MemoryStoresTab mgmtApi={mgmtApi} />
                    </TabsContent>
                    <TabsContent value="cs-reference-files">
                        <ReferenceFilesTab mgmtApi={mgmtApi} />
                    </TabsContent>
                    <TabsContent value="cs-skills">
                        <SkillsTab />
                    </TabsContent>
                    <TabsContent value="cs-vaults">
                        <VaultsTab mgmtApi={mgmtApi} />
                    </TabsContent>
                </Tabs>
            )}
        </div>
    );
};
